<script lang="ts">
	import PageGrid from '../components/pageGrid.svelte';
	import Carousel from '$lib/carousel.svelte';
	import type { EmblaCarouselType, EmblaOptionsType } from 'embla-carousel';
	import Instagram from '$lib/icons/instagram.svelte';
	import CarouselNavigationArrows from '$lib/carouselNavigationArrows.svelte';
	import SbRichtext from '../components/sbRichtext.svelte';
	import type { ISbRichtext, ISbStoryData } from '@storyblok/js';
	import { editable } from '../editable';
	import Blok from '../blok.svelte';
	import { innerWidth } from 'svelte/reactivity/window';

	interface Props {
		content: ISbStoryData<{
			headline: string | ISbRichtext;
			social_label: string;
			testimonials: Array<ISbStoryData['content']>;
			alignment: EmblaOptionsType['align'];
		}>['content'];
	}

	let { content }: Props = $props();

	const colorClasses: Record<string | number, string> = {
		stone: 'bg-stone-4 text-brown-1',
		0: 'bg-teal-5 text-teal-1',
		1: 'bg-orange-6 text-orange-1',
		2: 'bg-yellow-5 text-yellow-1',
		3: 'bg-green-6 text-green-1',
		4: 'bg-purple-5 text-purple-1'
	};

	let colorIndex = -1;
	let embla = $state<EmblaCarouselType>();
	let active = $state(0);

	let isMobile = $derived(!innerWidth.current || innerWidth.current < 768);

	function getNextColorClasses(): string {
		colorIndex = (colorIndex + 1) % 5;
		return colorClasses[colorIndex];
	}

	function next() {
		if (embla) {
			embla.scrollNext();
		}
	}

	function previous() {
		if (embla) {
			embla.scrollPrev();
		}
	}

	function getCarouselAlignment(alignment: EmblaOptionsType['align']) {
		if (alignment === 'start') {
			return 'lg:justify-start';
		}

		if (alignment === 'end') {
			return 'lg:justify-end';
		}

		return 'lg:justify-center';
	}

	let carouselClass = $derived(getCarouselAlignment(content.alignment));
</script>

<section use:editable={content} class="flex w-screen gap-x-4 overflow-x-hidden">
	<PageGrid>
		<div class="col-span-full mb-13 flex flex-col items-center text-center text-brown-2">
			{#if content.headline}
				<h1
					class="text-[32px]/[110%] font-semibold tracking-[-.32px] lg:text-[48px]/[110%] lg:tracking-[-.48px]"
				>
					<SbRichtext text={content.headline} />
				</h1>
			{/if}
			{#if content.social_label}
				<a
					target="_blank"
					class="mt-3 flex self-center text-[18px]/[130%] font-semibold"
					href="https://www.instagram.com/mill/"
				>
					<Instagram class="mr-1 aspect-square h-6 w-6" />{content.social_label}</a
				>
			{/if}
		</div>
		<div class="col-span-full">
			{#if isMobile}
				{@const testimonials = content.testimonials.slice(0, 5)}
				<div class="flex flex-col gap-y-5">
					{#each testimonials as testimonial}
						<Blok
							content={testimonial}
							colorClasses={testimonial.logo.filename || testimonial.image.filename
								? colorClasses['stone']
								: getNextColorClasses()}
						/>
					{/each}
				</div>
			{:else}
				<Carousel
					align={content.alignment || 'center'}
					bind:embla
					bind:currentIndex={active}
					class="flex gap-x-8 {carouselClass}"
					overflow
				>
					{#each content.testimonials as testimonial}
						<div class="flex-none basis-[80%] rounded-xl sm:basis-[40%] lg:basis-[25%]">
							<Blok
								content={testimonial}
								colorClasses={testimonial.logo.filename || testimonial.image.filename
									? colorClasses['stone']
									: getNextColorClasses()}
							/>
						</div>
					{/each}
				</Carousel>
			{/if}
		</div>

		<div
			class="col-span-full my-16 flex justify-center max-lg:hidden"
			class:hidden={content.testimonials.length < 4}
		>
			<CarouselNavigationArrows
				hasNext={active < content.testimonials.length - 3}
				hasPrev={active !== 0}
				onnext={next}
				onprevious={previous}
			/>
		</div>
	</PageGrid>
</section>
